import { useEffect, useState, type FormEvent } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import { api } from '../../api/client'
import { useToast } from '../../components/ui/Toast'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/Dialog'
import { FormActions, FormField } from '../../components/ui/FormField'
import { getApiErrorMessage } from '../../lib/api-errors'
import { useUserContext } from '../../providers/UserContextProvider'

interface ChangePasswordDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ChangePasswordDialog({ open, onOpenChange }: ChangePasswordDialogProps) {
  const { userDetail } = useUserContext()
  const { t } = useTranslation()
  const { toast } = useToast()

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [currentPasswordError, setCurrentPasswordError] = useState('')
  const [confirmError, setConfirmError] = useState('')

  useEffect(() => {
    if (open) {
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setCurrentPasswordError('')
      setConfirmError('')
    }
  }, [open])

  const mutation = useMutation({
    mutationFn: () => api.users.changePassword({ currentPassword, newPassword }),
    onSuccess: () => {
      toast({ title: t('preferences.account.password.changed'), variant: 'success' })
      onOpenChange(false)
    },
    onError: (error) => {
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        setCurrentPasswordError(t('preferences.account.password.incorrectCurrent'))
        return
      }
      toast({ title: t('preferences.account.password.changeFailed'), description: getApiErrorMessage(error), variant: 'error' })
    },
  })

  const isSubmitting = mutation.isPending

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setCurrentPasswordError('')
    setConfirmError('')
    if (newPassword.length < 8) {
      setConfirmError(t('preferences.account.password.tooShort'))
      return
    }
    if (newPassword !== confirmPassword) {
      setConfirmError(t('preferences.account.password.mismatch'))
      return
    }
    mutation.mutate()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('preferences.account.password.title')}</DialogTitle>
          <DialogDescription>{t('preferences.account.password.description')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            autoComplete="username"
            value={userDetail?.email ?? ''}
            readOnly
            hidden
          />
          <FormField id="change-password-current" label={t('preferences.account.password.current')} required error={currentPasswordError}>
            {({ fieldId, describedBy, invalid }) => (
              <input
                id={fieldId}
                type="password"
                autoComplete="current-password"
                className="input-field"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                aria-describedby={describedBy}
                aria-invalid={invalid}
              />
            )}
          </FormField>

          <FormField id="change-password-new" label={t('preferences.account.password.new')} required>
            {({ fieldId, describedBy, invalid }) => (
              <input
                id={fieldId}
                type="password"
                autoComplete="new-password"
                className="input-field"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                aria-describedby={describedBy}
                aria-invalid={invalid}
              />
            )}
          </FormField>

          <FormField id="change-password-confirm" label={t('preferences.account.password.confirm')} required error={confirmError}>
            {({ fieldId, describedBy, invalid }) => (
              <input
                id={fieldId}
                type="password"
                autoComplete="new-password"
                className="input-field"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                aria-describedby={describedBy}
                aria-invalid={invalid}
              />
            )}
          </FormField>

          <FormActions
            cancelLabel={t('common.cancel')}
            submitLabel={isSubmitting ? t('common.saving') : t('preferences.account.password.submit')}
            onCancel={() => onOpenChange(false)}
            isSubmitting={isSubmitting}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}
